import React, { useState } from 'react';
import SharedButton from '@/global/Button/Button';
import AcademicApplicationService from '@/services/AcademicApplicationService';
import { useAuthStore } from '@/store/authStore';
import { useRevertAcademicToTrial } from '@/hooks/useRevertAcademicToTrial';
import { getCurrentUser } from 'aws-amplify/auth';
import { refreshSession } from '@/services/RefreshSession';
import { enrichUserWithCognitoAttributes } from '@/utils/enrichUserWithCognitoAttributes';
import RevertToTrialModal from '@/components/User/RevertToTrialModal';

const ProfileAcademicTrialActions: React.FC = () => {
  const user = useAuthStore(state => state.user);
  const groups = useAuthStore(state => state.groups);
  const academicStatus = useAuthStore(state => state.academicStatus);
  const academicActivationDeferred = useAuthStore(state => state.academicActivationDeferred);
  const {
    canRevert,
    alreadyReverted,
    trialDaysRemaining,
    revertToTrial,
    loading: reverting,
    error,
  } = useRevertAcademicToTrial();
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [showRevertModal, setShowRevertModal] = useState(false);

  const isTrial = groups?.includes('ROLE_TRIAL');
  const showActivate =
    isTrial && academicStatus === 'approved' && academicActivationDeferred && Boolean(user?.email);
  const showBackToTrial = canRevert;
  const showAlreadyUsed = Boolean(groups?.includes('ROLE_ACADEMIC') && alreadyReverted);

  if (!showActivate && !showBackToTrial && !showAlreadyUsed) {
    return null;
  }

  const handleActivate = async () => {
    if (!user?.email) return;
    setActivating(true);
    setActivateError(null);
    try {
      const response = await AcademicApplicationService.activateAcademic(user.email);
      if (response.status === 'error') {
        throw new Error(response.message);
      }

      await new Promise(resolve => setTimeout(resolve, 1500));
      const session = await refreshSession({ forceRefresh: true });
      if (session?.tokens?.idToken?.payload) {
        const userInfo = session.tokens.idToken.payload as Record<string, unknown>;
        const userGroups = (userInfo['cognito:groups'] as string[]) || [];
        const currentUser = await getCurrentUser();
        const enriched = await enrichUserWithCognitoAttributes({
          ...userInfo,
          ...currentUser,
        });
        useAuthStore.getState().setUser(enriched);
        useAuthStore.getState().setGroups(userGroups);
        await useAuthStore.getState().fetchUserType();
      }
      sessionStorage.setItem('showAcademicUpgradeModal', '1');
      window.location.reload();
    } catch (err) {
      setActivating(false);
      setActivateError(err instanceof Error ? err.message : 'Please try again.');
    }
  };

  const handleConfirmRevert = async () => {
    try {
      await revertToTrial();
    } catch {
      // error surfaced via hook; keep modal open so user can cancel
    }
  };

  return (
    <>
      <div className="bg-white border rounded-xl p-4 mb-4">
        <p className="text-md text-uppercase font-semibold mb-2">Academic access</p>
        {showActivate && (
          <>
            <p className="text-sm text-gray-600 mb-3">
              Your academic application is approved. You can activate it now, or keep using your trial until
              it ends (academic access will activate automatically then).
            </p>
            {activateError && <p className="text-sm text-red-600 mb-2">{activateError}</p>}
            <SharedButton
              type="button"
              variant="gradient"
              size="sm"
              onClick={handleActivate}
              disabled={activating}
            >
              {activating ? 'Activating…' : 'Activate academic plan'}
            </SharedButton>
          </>
        )}
        {showBackToTrial && (
          <>
            <p className="text-sm text-gray-600 mb-3">
              You still have {trialDaysRemaining} day{trialDaysRemaining === 1 ? '' : 's'} left in your
              commercial trial. You can return to full trial access <strong>only once</strong>.
            </p>
            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
            <SharedButton
              type="button"
              variant="secondaryOutline"
              size="sm"
              onClick={() => setShowRevertModal(true)}
              disabled={reverting}
            >
              {reverting ? 'Restoring trial…' : 'Back to trial'}
            </SharedButton>
          </>
        )}
        {showAlreadyUsed && (
          <p className="text-sm text-gray-500 mb-0">
            You already used your one-time option to return to trial. Academic access stays active for the
            rest of this period.
          </p>
        )}
      </div>

      <RevertToTrialModal
        open={showRevertModal}
        daysRemaining={trialDaysRemaining}
        loading={reverting}
        error={error}
        onConfirm={handleConfirmRevert}
        onCancel={() => setShowRevertModal(false)}
      />
    </>
  );
};

export default ProfileAcademicTrialActions;
